import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, RotateCcw, Star, Timer, BellOff, Volume2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { IconRenderer } from "../lib/icons";
import { speak, playJingle } from "../lib/speech";
import Header from "../components/Header";
import UserNav from "../components/UserNav";

const today = () => new Date().toISOString().slice(0, 10);

export default function RoutineRunner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [routine, setRoutine] = useState(null);
  const [done, setDone] = useState([]);
  const [timersOn, setTimersOn] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [alerting, setAlerting] = useState(false);
  const [audioGuide, setAudioGuide] = useState(() => localStorage.getItem("rb_audio_guide") === "1");
  const [simpleMode, setSimpleMode] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [r, p] = await Promise.all([
          api.get(`/routines/${id}`),
          api.get(`/routines/${id}/progress`, { params: { date: today() } }),
        ]);
        setRoutine(r.data);
        setDone(p.data.done || []);
      } catch (e) {
        toast.error(apiError(e));
        navigate("/use");
      }
    })();
  }, [id, navigate]);

  const allDone = useMemo(() => routine && routine.steps.length > 0 && done.length === routine.steps.length, [routine, done]);

  const hasTimers = useMemo(() => routine?.steps.some((s) => s.minutes) ?? false, [routine]);
  const currentStep = timersOn && routine ? routine.steps.find((s) => s.minutes && !done.includes(s.id)) : null;
  const nextStep = currentStep ? routine.steps.slice(routine.steps.indexOf(currentStep) + 1).find((s) => s.minutes && !done.includes(s.id)) : null;
  const nextUndone = routine?.steps.find((s) => !done.includes(s.id));

  useEffect(() => {
    if (!currentStep) return;
    setSecondsLeft(currentStep.minutes * 60);
    setAlerting(false);
  }, [currentStep?.id, timersOn]);

  useEffect(() => {
    if (!timersOn || !currentStep || alerting || secondsLeft === null) return;
    if (secondsLeft <= 0) { setAlerting(true); return; }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timersOn, currentStep, alerting, secondsLeft]);

  useEffect(() => {
    if (!alerting || !currentStep) return;
    const msg = nextStep ? `Time for the next step: ${nextStep.label}` : "Time to finish. Well done!";
    playJingle(routine?.alarm_sound);
    speak(msg);
    const t = setInterval(() => { playJingle(routine?.alarm_sound); speak(msg); }, 30000);
    return () => clearInterval(t);
  }, [alerting, currentStep?.id, nextStep?.id]);

  // Audio guide (blind/low-vision): announce each step as it becomes current
  useEffect(() => {
    if (!audioGuide || !routine || !nextUndone) return;
    speak(`${nextUndone.label}. Step ${done.length + 1} of ${routine.steps.length}.`);
  }, [audioGuide, nextUndone?.id]);

  // Audio guide: countdown milestones
  useEffect(() => {
    if (!audioGuide || !timersOn || alerting || secondsLeft === null) return;
    if (secondsLeft === 300) speak("Five minutes left.");
    if (secondsLeft === 60) speak("One minute left.");
    if (secondsLeft === 10) speak("Ten seconds left.");
  }, [audioGuide, timersOn, alerting, secondsLeft]);

  const toggleAudioGuide = () => {
    setAudioGuide((v) => {
      const next = !v;
      localStorage.setItem("rb_audio_guide", next ? "1" : "0");
      if (next) speak("Audio guide on. I'll read each step out loud.");
      return next;
    });
  };

  const stopTimers = () => {
    setTimersOn(false);
    setAlerting(false);
    setSecondsLeft(null);
    window.speechSynthesis?.cancel();
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const toggle = async (step) => {
    const next = done.includes(step.id) ? done.filter((d) => d !== step.id) : [...done, step.id];
    setDone(next);
    if (!done.includes(step.id)) {
      speak(audioGuide ? `Done. ${next.length} of ${routine.steps.length} steps done.` : step.label);
      if (audioGuide && next.length === routine.steps.length) setTimeout(() => speak("All done! Well done — you finished every step."), 1600);
    }
    try {
      await api.post(`/routines/${id}/progress`, { date: today(), done: next });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const reset = async () => {
    setDone([]);
    stopTimers();
    setSimpleMode(false);
    try {
      await api.post(`/routines/${id}/progress`, { date: today(), done: [] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!routine) return <div className="min-h-screen bg-background"><Header /><p className="p-8 text-lg">Loading…</p></div>;

  const pct = routine.steps.length ? Math.round((done.length / routine.steps.length) * 100) : 0;

  return (
    <div data-testid="routine-runner" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-2xl mx-auto px-4 py-8 pb-32">
        <button data-testid="back-button" onClick={() => navigate("/use")} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/25 bg-white font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
          <ArrowLeft size={20} strokeWidth={2.5} /> Back
        </button>

        <h1 className="mt-6 text-3xl font-extrabold tracking-tight">{routine.name}</h1>

        <div className="mt-4">
          <p className="text-lg font-bold">{done.length} of {routine.steps.length} done</p>
          <div data-testid="progress-bar" className="mt-2 h-4 rounded-full bg-secondary border-2 border-foreground/10 overflow-hidden">
            <div className="h-full bg-[#48BB78] rounded-full transition-[width] duration-200" style={{ width: `${pct}%` }} />
          </div>
        </div>

        {!allDone && (
          <button
            data-testid="simple-view-toggle"
            onClick={() => setSimpleMode((v) => !v)}
            className="mt-4 w-full min-h-[56px] rounded-full border-2 border-foreground/25 bg-white font-bold text-lg hover:bg-secondary transition-colors duration-200"
          >
            {simpleMode ? "Show all steps" : "Simple view — one step at a time"}
          </button>
        )}

        <button
          data-testid="audio-guide-toggle"
          onClick={toggleAudioGuide}
          aria-pressed={audioGuide}
          className={`mt-3 w-full min-h-[56px] rounded-full font-bold text-lg flex items-center justify-center gap-2 transition-colors duration-200 ${
            audioGuide ? "bg-primary text-white" : "border-2 border-foreground/25 bg-white hover:bg-secondary"
          }`}
        >
          <Volume2 size={24} strokeWidth={2.5} /> {audioGuide ? "Audio guide on" : "Audio guide — reads steps aloud"}
        </button>

        {allDone && (
          <div data-testid="all-done-message" className="mt-6 bg-[#48BB78]/15 border-2 border-[#48BB78] rounded-3xl p-6 flex items-center gap-4">
            <Star size={44} strokeWidth={2.5} className="text-[#2F855A] shrink-0" />
            <div>
              <p className="text-2xl font-extrabold text-[#22543D]">All done!</p>
              <p className="text-lg font-semibold text-[#22543D]">Well done — you finished every step.</p>
            </div>
          </div>
        )}

        {hasTimers && !allDone && (
          <div className="mt-6">
            {!timersOn ? (
              <button
                data-testid="start-timers-button"
                onClick={() => setTimersOn(true)}
                className="w-full min-h-[64px] rounded-full bg-[#ED8936] text-white text-xl font-bold flex items-center justify-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-px"
              >
                <Timer size={26} strokeWidth={2.5} /> Start step timers
              </button>
            ) : (
              <button
                data-testid="stop-timers-button"
                onClick={stopTimers}
                className="w-full min-h-[56px] rounded-full border-2 border-foreground/25 bg-white text-lg font-bold flex items-center justify-center gap-2 hover:bg-secondary transition-colors duration-200"
              >
                <BellOff size={22} strokeWidth={2.5} /> Stop timers
              </button>
            )}
          </div>
        )}

        {alerting && (
          <div data-testid="next-step-alert" className="alarm-flash mt-6 bg-white border-4 rounded-3xl p-6 text-center">
            <p className="text-2xl font-extrabold">Time for the next step!</p>
            <p className="mt-1 text-xl font-bold text-primary">{nextStep ? nextStep.label : "Finish up — you're nearly done!"}</p>
          </div>
        )}

        {simpleMode && !allDone && nextUndone && (
          <div data-testid="now-next-card" className="mt-6 bg-white rounded-3xl border-2 border-foreground/15 p-8 text-center">
            <span className="mx-auto w-40 h-40 rounded-3xl bg-primary/10 grid place-items-center overflow-hidden">
              {nextUndone.image ? <img src={nextUndone.image} alt="" className="w-full h-full object-cover" /> : <IconRenderer name={nextUndone.icon} size={96} className="text-primary" />}
            </span>
            <p className="mt-6 text-4xl font-extrabold leading-tight">{nextUndone.label}</p>
            <p className="mt-2 text-lg font-bold text-muted-foreground">{done.length} of {routine.steps.length} done</p>
            <button
              data-testid="simple-done-button"
              onClick={() => toggle(nextUndone)}
              className="mt-6 w-full min-h-[80px] rounded-full bg-[#48BB78] text-white text-2xl font-extrabold flex items-center justify-center gap-3 transition-transform duration-200 ease-out hover:-translate-y-px active:scale-95"
            >
              <Check size={36} strokeWidth={3.5} /> Done
            </button>
            {done.length > 0 && (
              <button
                data-testid="undo-last-step-button"
                onClick={() => {
                  const lastDone = routine.steps.find((s) => s.id === done[done.length - 1]);
                  if (lastDone) toggle(lastDone);
                }}
                className="mt-3 w-full min-h-[56px] rounded-full border-2 border-foreground/25 bg-white text-lg font-bold flex items-center justify-center gap-2 hover:bg-secondary transition-colors duration-200"
              >
                <RotateCcw size={22} strokeWidth={2.5} /> Oops — undo last step
              </button>
            )}
          </div>
        )}

        {!simpleMode && (<>
        <div className="mt-6 space-y-4">
          {routine.steps.map((step, i) => {
            const isDone = done.includes(step.id);
            const isCurrent = timersOn && currentStep?.id === step.id;
            const isNext = alerting && nextStep?.id === step.id;
            return (
              <button
                key={step.id}
                data-testid={`routine-step-${step.id}`}
                onClick={() => toggle(step)}
                aria-pressed={isDone}
                aria-label={`Step ${i + 1}: ${step.label}${isDone ? ", completed. Activate to mark not done." : ". Activate to mark done."}`}
                className={`w-full rounded-3xl border-2 p-5 flex items-center gap-5 text-left transition-colors duration-200 ${
                  isDone ? "bg-[#48BB78]/15 border-[#48BB78]" : isCurrent ? "bg-white border-[#ED8936] border-4" : isNext ? "bg-white alarm-flash border-4" : "bg-white border-foreground/15"
                }`}
              >
                <span className={`w-20 h-20 rounded-2xl grid place-items-center shrink-0 overflow-hidden ${isDone ? "bg-[#48BB78]/20" : "bg-primary/10"}`}>
                  {step.image ? <img src={step.image} alt="" className="w-full h-full object-cover" /> : <IconRenderer name={step.icon} size={44} className={isDone ? "text-[#2F855A]" : "text-primary"} />}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-bold text-muted-foreground">Step {i + 1}</span>
                  <span className={`block text-2xl font-extrabold leading-tight ${isDone ? "line-through text-[#2F855A]" : ""}`}>{step.label}</span>
                  {isCurrent && (
                    <span data-testid="step-countdown" className="mt-1 inline-flex items-center gap-1 text-lg font-extrabold text-[#C05621]">
                      <Timer size={20} strokeWidth={2.5} /> {alerting ? "Time's up!" : secondsLeft === null ? "…" : fmt(secondsLeft)}
                    </span>
                  )}
                </span>
                <span
                  data-testid={`step-check-${step.id}`}
                  className={`w-14 h-14 rounded-full border-4 grid place-items-center shrink-0 transition-colors duration-200 ${
                    isDone ? "bg-[#48BB78] border-[#48BB78]" : "border-foreground/25 bg-white"
                  }`}
                >
                  {isDone && <Check size={32} strokeWidth={4} className="text-white" />}
                </span>
              </button>
            );
          })}
        </div>

        <button
          data-testid="reset-routine-button"
          onClick={reset}
          className="mt-8 w-full min-h-[56px] rounded-full border-2 border-foreground/25 bg-white font-bold text-lg flex items-center justify-center gap-2 hover:bg-secondary transition-colors duration-200"
        >
          <RotateCcw size={22} strokeWidth={2.5} /> Start again
        </button>
        </>)}
      </main>
      <UserNav />
    </div>
  );
}
